import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { traverse } from 'object-traversal';
import pLimit from 'p-limit';
import { Observable, Subject } from 'rxjs';
import FormDefinitionControl from 'src/app/models/form-definition/form-control';
import FormDefinition from 'src/app/models/form-definition/form-definition';
import { LogService } from '../baseServices/logging/log.service';
import { ChoiceOption } from '../components/form-elements/choiceinput/choiceinput.component';
import { GlobalConstants } from '../constant/constant';
import { FormDocumentFile } from '../entities/FormDocumentFile';
import { StoreName } from '../entities/StoreName';
import { SyncStatus } from '../entities/SyncStatus';
import { FORM_CONTROL_TYPES } from '../enums/general.enum';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import FormDocument, { FormDocumentStatus } from '../models/form-document/form-document';
import { FormDocumentIdentifier } from '../models/form-document/form-document-identifier';
import { FormDocumentMetadata } from '../models/form-document/form-document-metadata';
import { FormDocumentSummary } from '../models/form-document/form-document-summary';
import FormDocumentValue from '../models/form-document/form-document-value';
import { DateTimeHelper } from '../utils/date-time-helper';
import { AppStateService } from './app-state.service';
import { DataService } from './data.service';
import { DeviceInfoService } from './device-info.service';
import { FormDefinitionsService } from './form-definitions.service';
import { FormDocumentMediaService } from './form-document-media.service';
import { FormDocumentVideoUploadService } from './form-document-video-upload.service';
import { PhotoService } from './photo.service';
import { SyncNotificationService } from './syncNotificationService';

type Modify<T, R> = Omit<T, keyof R> & R;
@Injectable({
  providedIn: 'root',
})
export class FormDocumentsService extends DataService {
  documentUpdated: Observable<any>;
  private emitDocumentChange = new Subject<any>();

  constructor(
    private syncNotificationService: SyncNotificationService,
    private formDefinitionService: FormDefinitionsService,
    private formDocumentMediaService: FormDocumentMediaService,
    private photoService: PhotoService,
    private videoService: FormDocumentVideoUploadService,
    private deviceInfoService: DeviceInfoService,
    private appStateService: AppStateService,
    private logService: LogService
  ) {
    super();
    this.documentUpdated = this.emitDocumentChange.asObservable();

    this.syncNotificationService.onStatusChange$.subscribe(async (data: any) => {
      if (data && data.EntityName && data.EntityName.toLowerCase() === StoreName.FormDocuments.toLowerCase()) {
        setTimeout(() => {
          this.emitChange();
        }, 500);
      }
    });
  }

  emitChange(inactivatedFormDefinitions?: FormDefinition[]) {
    this.emitDocumentChange.next(inactivatedFormDefinitions);
  }

  /** Builds standard log context for a form document with optional extras */
  private buildLogContext(formDocument: FormDocument | undefined, extra: Record<string, any> = {}) {
    if (!formDocument) return { ...extra };
    return {
      documentId: formDocument.DocumentId,
      formDefinitionId: formDocument.FormDefinition?.id,
      identifiers: formDocument.Identifiers,
      ...extra,
    };
  }


  async getFormDocumentsSummaryByBaseFormDefinitionId(baseFormDefinitionId: number): Promise<FormDocumentSummary[]> {
    return await this.repository.getAllPartialFieldsByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, baseFormDefinitionId, FormDocumentSummary);
  }

  async getAllFormDocumentsMetadata(): Promise<FormDocumentMetadata[]> {
    return await this.repository.getAllPartialFields(StoreName.FormDocuments, FormDocumentMetadata);
  }

  async getFormDocumentsMetadataByProjectId(projectId: number): Promise<FormDocumentMetadata[]> {
    const formDefinitions = await this.formDefinitionService.getAllByProject(projectId);
    const baseFormDefinitionIds = formDefinitions.map(formDefinition => formDefinition.baseFormDefinitionId);
    return await this.repository.getAllPartialFieldsByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, baseFormDefinitionIds, FormDocumentMetadata);
  }

  async getFormDocumentsMetadataByBaseFormDefinitionId(baseFormDefinitionId: number): Promise<FormDocumentMetadata[]> {
    return await this.repository.getAllPartialFieldsByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, baseFormDefinitionId, FormDocumentMetadata);
  }

  async getOne(documentId: string, existingTransaction?: DbTransaction): Promise<FormDocument> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.repository.getOneByIndex<FormDocument>(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.DocumentId, documentId, undefined, transaction);
  }

  async getLastSubmittedDocumentByBaseFormDefinitionId(baseFormDefinitionId: number): Promise<FormDocument | undefined> {
    const documentsMetadata = await this.getFormDocumentsMetadataByBaseFormDefinitionId(baseFormDefinitionId);

    const sortedSubmittedDocumentMetadata = documentsMetadata
      .filter(doc => doc.Status === FormDocumentStatus.Submitted)
      .sort((a, b) => {
        const aDate = new Date(a.ModifiedDate ?? a.CreatedDate);
        const bDate = new Date(b.ModifiedDate ?? b.CreatedDate);
        return bDate.getTime() - aDate.getTime(); // Descending
      });

    if (sortedSubmittedDocumentMetadata.length === 0) {
      return undefined;
    }

    return this.getOne(sortedSubmittedDocumentMetadata[0].DocumentId);
  }

  async create(formDefinitionId: number): Promise<FormDocument> {
    const formDefinition = await this.formDefinitionService.getOne(formDefinitionId);
    const RECORD_ID = 1;
    const deviceInfo = await this.repository.getByLocalId(StoreName.DeviceIdAndDocumentIdSequence, RECORD_ID);
    const deviceID = deviceInfo.deviceID;
    const docIDSuffix = deviceInfo.documentIDSuffix;
    await this.repository.update(
      {
        RecordId: RECORD_ID,
        deviceID,
        documentIDSuffix: docIDSuffix + 1,
      },
      StoreName.DeviceIdAndDocumentIdSequence
    );
    const documentNumber = deviceID + '-' + formDefinition.docTypeCode + '-' + docIDSuffix.toString(36).toUpperCase();

    const formDocument: FormDocument = {
      FormDefinition: formDefinition,
      FormDefinitionBaseId: formDefinition.baseFormDefinitionId,
      FormVersion: formDefinition.version,
      DocumentId: documentNumber,
      CreatedDate: new Date().toISOString(),
      Status: FormDocumentStatus.New,
      syncStatus: SyncStatus.NotSet,
      FormComponents: [],
      ActiveStepIndex: 1,
      artifactGenerated: false,
      Identifiers: [],
    };
    await this.repository.create(formDocument, StoreName.FormDocuments);

    this.emitChange();
    this.logService.event('FormDocument.Create', this.buildLogContext(formDocument));

    return await this.getOne(documentNumber);
  }

  async save(formDocument: FormDocument, statusChanged: boolean = false): Promise<void> {
    formDocument.ModifiedDate = DateTimeHelper.getDateTime().toISOString();
    formDocument.Identifiers = this.getIdentifiers(formDocument);
    await this.repository.update(formDocument, StoreName.FormDocuments);
    if (statusChanged) {
      this.emitChange();
    }
  }

  async delete(formDocument: FormDocument): Promise<void> {
    await this.repository.delete(formDocument.RecordId, StoreName.FormDocuments);
    this.emitChange();
    this.logService.event('FormDocument.Delete', this.buildLogContext(formDocument));
  }

  async submit(formDocument: FormDocument): Promise<void> {
    try {
      formDocument.Status = FormDocumentStatus.Submitted;
      formDocument.syncStatus = SyncStatus.Ready;
      formDocument.ModifiedDate = DateTimeHelper.getDateTime().toISOString();
      await this.repository.update(formDocument, StoreName.FormDocuments);
      this.emitChange();
      this.logService.event('FormDocument.Submit', this.buildLogContext(formDocument));

      setTimeout(() => {
        this.sync(formDocument.DocumentId);
      }, 10000);

      /** TODO Convert audio files to base64 - remove audio files from device when document is synced */
    } catch (err) {
      this.logService.error('FormDocument.SubmitError', err, this.buildLogContext(formDocument));
    }
  }

  async sync(documentId: string): Promise<void> {
    const formDocument = await this.getOne(documentId);

    if (formDocument && formDocument.Status === FormDocumentStatus.Submitted) {
      formDocument.syncStatus = SyncStatus.Ready;
      formDocument.ModifiedDate = new Date().toISOString();
      await this.repository.update(formDocument, StoreName.FormDocuments);
      await this.upload(formDocument);

      await this.deleteSyncedDocuments();
    }
  }

  async deleteSyncedDocuments(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const todayDate = DateTimeHelper.getDateTime();
    const thresholdDate = DateTimeHelper.addTime(todayDate, -GlobalConstants.DELETE_DOCUMENTS_DATE_THRESHOLD_DAYS, 'days');

    const key: DbKeyType = {
      lowerOpen: true,
      lower: [SyncStatus.Completed],
      upper: [SyncStatus.Completed, thresholdDate.toISOString()],
    };

    const documents: FormDocument[] = await this.repository.getAllByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus_ModifiedDate, key, undefined, transaction);

    await this.repository.deleteBulkData(documents, StoreName.FormDocuments);

    // Delete media files
    await this.formDocumentMediaService.deleteFormDocumentMediaByDocumentIds(documents.map(d => d.DocumentId));

    this.emitChange();
  }

  updateFormsSummary() {
    this.emitChange();
  }

  private getIdentifiers(formDocument: FormDocument): FormDocumentIdentifier[] {
    const identifiers: FormDocumentIdentifier[] = [];
    const businessIdKeys = formDocument.FormDefinition.businessIdKeys || [];
    const formComponents = formDocument.FormComponents;
    const formControls = formDocument.FormDefinition.steps.flatMap(step => step.controls);
    businessIdKeys.forEach((key: string) => {
      let identifierKey: string = key;
      let identifierAttribute: string = '';
      if (key.includes('.')) {
        const keyParts = key.split('.');
        identifierKey = keyParts[0];
        identifierAttribute = keyParts[1];
      }
      const component = formComponents.find(component => component.key === identifierKey);
      const formControl = formControls.find(control => control.key === identifierKey);
      if (component && formControl) {
        identifiers.push({
          label: formControl.label,
          value: component.value,
          attribute: identifierAttribute,
        });
      }
    });
    return identifiers;
  }

  private async update(document: FormDocument, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    await this.repository.update(document, StoreName.FormDocuments, transaction);
  }

  private async mapFormDocumentToAPIObject(formDocument: FormDocument): Promise<
    Modify<
      FormDocument,
      {
        FormComponents: any;
        FormDefinitionId?: number;
      }
    >
  > {
    function processGroupItems(groupControl: FormDefinitionControl, groupValue: any) {
      groupControl.type.groupControls
        ?.filter(subControl => !GlobalConstants.VISUAL_CONTROL_TYPES.includes(subControl.type.inputType as FORM_CONTROL_TYPES))
        .forEach(subControl => {
          if (groupValue.hasOwnProperty(subControl.key)) {
            if (subControl.type.inputType === FORM_CONTROL_TYPES.CHOICE) {
              if (groupValue[subControl.key] && Array.isArray(groupValue[subControl.key])) {
                groupValue[subControl.key] = groupValue[subControl.key].map((v: ChoiceOption) => {
                  if (v.attributes && v.attributes.images) {
                    delete v.attributes.images;
                  }
                  return v;
                });
              }
            }
            groupValue[subControl.uniqueName] = groupValue[subControl.key];
            delete groupValue[subControl.key];
          }

          // Check if the sub-control itself is a GROUP and process nested items
          if (subControl.type.inputType === FORM_CONTROL_TYPES.GROUP && groupValue[subControl.uniqueName] && Array.isArray(groupValue[subControl.uniqueName].items)) {
            groupValue[subControl.uniqueName].items.forEach(nestedItem => {
              if (nestedItem && typeof nestedItem === 'object') {
                processGroupItems(subControl, nestedItem);
              }
            });
          }
        });
    }

    const azureBlobStoragePromises = [];
    const concurrentUploadLimit = pLimit(10);
    traverse(formDocument.FormComponents, async ({ value }) => {
      if (value && value.formDocumentMediaRecordId && value.syncStatus !== SyncStatus.Completed) {
        const file = value as FormDocumentFile;

        switch (file.type) {
          case 'image':
            azureBlobStoragePromises.push(
              concurrentUploadLimit(async () => {
                try {
                  const media = await this.formDocumentMediaService.getFormDocumentMediaByRecordId(file.formDocumentMediaRecordId);
                  const token = await this.photoService.uploadPhoto(file.fileName, media.Value);
                  file.uriToken = token;
                  file.syncStatus = SyncStatus.Completed;
                  this.logService.event('FormDocument.ImageUploadSuccess', this.buildLogContext(formDocument, { fileName: file.fileName }));
                } catch (err) {
                  file.syncStatus = SyncStatus.Error;
                  this.logService.error('FormDocument.ImageUploadError', err, this.buildLogContext(formDocument, { fileName: file.fileName }));
                  throw err;
                }
              })
            );
            break;
          case 'video':
            azureBlobStoragePromises.push(
              concurrentUploadLimit(async () => {
                try {
                  const media = await this.formDocumentMediaService.getFormDocumentMediaByRecordId(file.formDocumentMediaRecordId);
                  const token = await this.videoService.uploadVideo(file.fileName, media.Value);
                  file.uriToken = token;
                  file.syncStatus = SyncStatus.Completed;
                  this.logService.event('FormDocument.VideoUploadSuccess', this.buildLogContext(formDocument, { fileName: file.fileName }));
                } catch (err) {
                  file.syncStatus = SyncStatus.Error;
                  this.logService.error('FormDocument.VideoUploadError', err, this.buildLogContext(formDocument, { fileName: file.fileName }));
                  throw err;
                }
              })
            );
            break;
        }
      }
    });

    try {
      await Promise.all(azureBlobStoragePromises);
      const mappedObject: Modify<FormDocument, { FormComponents: any; FormDefinitionId?: number }> = cloneDeep(formDocument);

      traverse(mappedObject.FormComponents, ({ value }) => {
        if (value && typeof value === 'object' && value.hasOwnProperty('formDocumentMediaRecordId')) {
          delete value.formDocumentMediaRecordId;
          delete value.syncStatus;
          delete value.webviewPath;
        }
      });

      const formComponents = {};
      mappedObject.FormComponents.forEach((component: FormDocumentValue) => {
        let objKey: string;
        if (component.uniqueName) {
          objKey = component.uniqueName;
        } else {
          objKey = component.key;
        }

        if (component.type.inputType === FORM_CONTROL_TYPES.CHOICE) {
          if (component.value && component.value.length > 0) {
            component.value = component.value.map((v: ChoiceOption) => {
              if (v.attributes && v.attributes.images) {
                delete v.attributes.images;
                return v;
              } else {
                return v;
              }
            });
          }
        }

        if (component.value && component.value.items && Array.isArray(component.value.items)) {
          const step = formDocument.FormDefinition.steps.find(s => {
            const foundControl = s.controls.find(c => c.type.inputType === FORM_CONTROL_TYPES.GROUP && c.key === component.key);
            return foundControl !== undefined;
          });

          if (step) {
            const foundGroupControl = step.controls.find(c => c.type.inputType === FORM_CONTROL_TYPES.GROUP && c.key === component.key);

            if (foundGroupControl) {
              // Handle parent controls
              foundGroupControl.type.parentControls?.forEach(parentControl => {
                if (component.value[parentControl.key]) {
                  component.value[parentControl.uniqueName] = component.value[parentControl.key];
                  delete component.value[parentControl.key];
                }
              });

              // Handle items recursively
              component.value.items.forEach(subItem => {
                if (subItem && typeof subItem === 'object') {
                  processGroupItems(foundGroupControl, subItem);
                }
              });
            }
          }
        }

        if (component.tags) {
          formComponents[objKey] = {
            value: component.value,
            tags: component.tags,
          };
        } else {
          formComponents[objKey] = component.value;
        }
      });

      mappedObject.FormComponents = formComponents;
      mappedObject.FormDefinitionId = mappedObject.FormDefinition.id;

      // Remove redundant attributes from form document
      delete mappedObject.ActiveStepIndex;
      delete mappedObject.FormDefinition;
      delete mappedObject.RecordId;
      delete mappedObject.Status;
      delete mappedObject.syncStatus;
      delete mappedObject.artifactGenerated;

      return mappedObject;
    } catch (err) {
      throw err;
    }
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.FormDocuments], 'readwrite'));
  }

  async revertInProgressDocuments(revertStuckDocuments = false) {
    let documents: FormDocument[] = [];

    if (revertStuckDocuments) {
      const now = DateTimeHelper.getDateTime();
      const thresholdDate = DateTimeHelper.addTime(now, -GlobalConstants.IN_PROGRESS_DOCUMENTS_TIME_THRESHOLD_MS, 'millisecond');

      const key: DbKeyType = {
        lowerOpen: true,
        lower: [SyncStatus.InProgress],
        upper: [SyncStatus.InProgress, thresholdDate.toISOString()],
      };

      documents = await this.repository.getAllByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus_ModifiedDate, key);
    } else {
      documents = await this.repository.getAllByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus, SyncStatus.InProgress);
    }

    for (const document of documents) {
      document.syncStatus = SyncStatus.Error;
      await this.update(document);
      this.logService.warn('FormDocument.RevertInProgress', this.buildLogContext(document));
    }
  }

  async assignDeviceIdAndDocumentIdSequence() {
    const userDeviceIdAndDocumentIdSequence = await this.repository.getByLocalId(StoreName.DeviceIdAndDocumentIdSequence, 1);
    if (!userDeviceIdAndDocumentIdSequence) {
      const deviceId = await this.deviceInfoService.getDeviceId();
      await this.repository.update(
        {
          RecordId: 1,
          deviceID: deviceId,
          documentIDSuffix: GlobalConstants.DOCUMENT_ID_SEQUENCE_START_BASE36,
        },
        StoreName.DeviceIdAndDocumentIdSequence
      );
      this.logService.event('FormDocument.AssignDeviceId', { deviceId });
    }
  }

  async syncAll(): Promise<void> {
    const isNetworkAvailable = this.appStateService.getCurrentNetworkStatus();
    if (isNetworkAvailable) {
      const readyToSyncItems = await this.repository.getAllPartialFieldsByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus, SyncStatus.Ready, FormDocumentMetadata);
      const failedItems = await this.repository.getAllPartialFieldsByIndex(StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus, SyncStatus.Error, FormDocumentMetadata);

      const recordsToUpload = readyToSyncItems.concat(failedItems);

      if (recordsToUpload.length > 0) {
        for (const record of recordsToUpload) {
          const formDocument = await this.getOne(record.DocumentId);
          await this.upload(formDocument);
        }

        await this.deleteSyncedDocuments();
      }
    }
  }

  async upload(element: FormDocument) {
    try {
      this.syncNotificationService.emitChange({
        syncProcess: 'upload',
        Status: 'Inprogress',
        EntityName: StoreName.FormDocuments,
        RecordId: element.RecordId,
        Id: element.RecordId,
      });

      element.syncStatus = SyncStatus.InProgress;

      await this.update(element);

      const lastFormDefinition = await this.formDefinitionService.getOneByBaseDefId(element.FormDefinitionBaseId);

      if (!lastFormDefinition || lastFormDefinition.version !== element.FormVersion) {
        element.syncStatus = SyncStatus.Invalid;
        element.Status = FormDocumentStatus.Submitted;
        element.ActiveStepIndex = 1;
        await this.update(element);
        return;
      }
      const mappedObject = await this.mapFormDocumentToAPIObject(element);

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
      });

      await this.http.post(this.FLEX_FORMS_API_ENDPOINTS.UploadDocuments, mappedObject, headers).toPromise();

      element.syncStatus = SyncStatus.Completed;
      await this.update(element);

      this.logService.info('FormDocument.Upload', this.buildLogContext(element), true);
    } catch (err) {
      this.logService.error('FormDocument.UploadError', err, this.buildLogContext(element));

      if (err.message.includes(GlobalConstants.SYNC_DUPLICATE_ID_ERROR)) {
        element.syncStatus = SyncStatus.Completed;
        await this.update(element);
      } else {
        element.syncStatus = SyncStatus.Error;
        await this.update(element);
      }

      this.syncNotificationService.emitChange({
        syncProcess: 'upload',
        Status: 'Error',
        EntityName: StoreName.FormDocuments,
        RecordId: element.RecordId,
        isNetworkAvailable: err.error instanceof ProgressEvent ? false : true,
      });
    }
  }
}
